import React, { useState } from "react";
import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { IDiseaseItem, IHealthAreaItem } from "../../../api/mosaic/health_area";
import { editButtonSx } from "../../shared/buttonStyles";
import SearchBar from "../../shared/SearchBar";

interface DiseaseBurdensTableProps {
  isLoading: boolean;
  diseasesData: IDiseaseItem[];
  healthAreas: IHealthAreaItem[];
  handleEdit: (disease: IDiseaseItem) => void;
}

interface IHealthAreaMapper {
  [key: number]: string;
}

const DiseaseBurdensTable: React.FC<DiseaseBurdensTableProps> = ({
  isLoading,
  diseasesData,
  healthAreas,
  handleEdit,
}) => {
  const [search, setSearch] = useState("");

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (diseasesData.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 5 }}>
        <Typography variant="body2" color="text.secondary">No diseases available</Typography>
      </Box>
    );
  }

  const healthAreaMapper = healthAreas.reduce((acc: IHealthAreaMapper, cur: IHealthAreaItem) => {
    acc[cur.id] = cur.name;
    return acc;
  }, {} as IHealthAreaMapper);

  const getActiveFilters = (filters: IDiseaseItem["filters"]) => {
    const active: string[] = [];
    if (filters?.measure_type) active.push("Measure Type");
    if (filters?.race) active.push("Race");
    if (filters?.sex) active.push("Sex");
    if (filters?.age) active.push("Age");
    return active;
  };

  const filteredData = diseasesData.filter((row) => {
    const term = search.toLowerCase();
    return (
      row.name.toLowerCase().includes(term) ||
      row.icode.toLowerCase().includes(term) ||
      row.source?.toLowerCase().includes(term) ||
      healthAreaMapper[row.healthAreaCategoryId]?.toLowerCase().includes(term)
    );
  });

  return (
    <>
    <SearchBar value={search} onChange={setSearch} placeholder="Search diseases..." />
    <TableContainer sx={{ flex: 1, overflow: "auto" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Icode</TableCell>
            <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Health Area</TableCell>
            <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Source</TableCell>
            <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Year</TableCell>
            <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Filters</TableCell>
            <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.map((disease) => (
            <TableRow key={disease.id} hover>
              <TableCell>{disease.name}</TableCell>
              <TableCell>{disease.icode}</TableCell>
              <TableCell>{healthAreaMapper[disease.healthAreaCategoryId]}</TableCell>
              <TableCell>{disease.source}</TableCell>
              <TableCell>{disease.sourceYear}</TableCell>
              <TableCell>
                <Chip label={disease.dataType} size="small" />
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {getActiveFilters(disease.filters).map((f) => (
                    <Chip key={f} label={f} size="small" variant="outlined" />
                  ))}
                </Box>
              </TableCell>
              <TableCell align="center">
                <Button variant="contained" onClick={() => handleEdit(disease)} sx={editButtonSx}>
                  Edit
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  );
};

export default DiseaseBurdensTable;
